import type { LoanStatus, LoanTerm } from "./types";

/**
 * Travel Now · Pay Later helpers shared by the applicant flow and the partner portal. Pure
 * functions, exported so the Vitest suite can reach them without rendering.
 */

// --- CURP -------------------------------------------------------------------------------------
// Mirrors apps/api/src/loans/curp.ts. Structural validation only (format, state code, check
// digit) — never presented as an official RENAPO verification.
const CURP_ALPHABET = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ";
const STATE_CODES = new Set([
  "AS", "BC", "BS", "CC", "CL", "CM", "CS", "CH", "DF", "DG", "GT", "GR", "HG", "JC", "MC", "MN", "MS", "NT", "NL", "OC", "PL",
  "QT", "QR", "SP", "SL", "SR", "TC", "TS", "TL", "VZ", "YN", "ZS", "NE"
]);
const CURP_RE = /^[A-Z][AEIOUX][A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9]\d$/;

export function normalizeCurp(value: string): string {
  return value.toUpperCase().replace(/Ñ/g, "X").replace(/[^A-Z0-9]/g, "").slice(0, 18);
}

export function curpCheckDigit(first17: string): string {
  let sum = 0;
  for (let index = 0; index < 17; index += 1) sum += CURP_ALPHABET.indexOf(first17[index]!) * (18 - index);
  return String((10 - (sum % 10)) % 10);
}

export function isValidCurp(value: string): boolean {
  const curp = normalizeCurp(value);
  if (curp.length !== 18 || !CURP_RE.test(curp)) return false;
  if (!STATE_CODES.has(curp.slice(11, 13))) return false;
  return curpCheckDigit(curp.slice(0, 17)) === curp[17];
}

/** `COGD••••••HDF•••` */
export function maskCurp(curp: string): string {
  const normalized = normalizeCurp(curp);
  if (normalized.length !== 18) return "—";
  return `${normalized.slice(0, 4)}••••••${normalized.slice(10, 13)}•••`;
}

// --- Amounts ----------------------------------------------------------------------------------
/** Whole pesos only: strips everything but digits and caps at 7 digits (the maximum is $500,000). */
export function sanitizeAmount(value: string): string {
  return value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "").slice(0, 7);
}

/** `$120,000 MXN` — the design formats amounts with US-style grouping and an explicit currency. */
export function formatMxn(amount: number): string {
  return `$${amount.toLocaleString("en-US")} MXN`;
}

export function amountError(raw: string, min: number, max: number): string | null {
  if (!raw) return "Ingresa la cantidad que deseas solicitar.";
  const amount = Number(raw);
  if (amount < min) return `Ingresa un monto de al menos ${formatMxn(min)}.`;
  if (amount > max) return `El monto máximo que puedes solicitar es de ${formatMxn(max)}.`;
  return null;
}

/** Annuity payment for a fixed-rate loan. Reference only: Peso a Peso sets the real terms. */
export function estimateMonthlyPayment(amount: number, termMonths: number, annualRatePercent: number): number {
  if (amount <= 0 || termMonths <= 0) return 0;
  const r = annualRatePercent / 100 / 12;
  if (r <= 0) return Math.round(amount / termMonths);
  return Math.round((amount * r) / (1 - Math.pow(1 + r, -termMonths)));
}

// --- Phone ------------------------------------------------------------------------------------
export function sanitizePhone(value: string): string {
  const plus = value.trim().startsWith("+") ? "+" : "";
  return plus + value.replace(/[^0-9]/g, "").slice(0, 15);
}

export function isValidPhone(value: string): boolean {
  return /^\+?[0-9]{10,15}$/.test(sanitizePhone(value));
}

/** `+52 55 4182 9037` for a Mexican mobile; other lengths are shown as typed. */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return "—";
  const digits = value.replace(/[^0-9]/g, "");
  if (digits.length === 12 && digits.startsWith("52")) return `+52 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  if (digits.length === 10) return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  return value;
}

// --- Status -----------------------------------------------------------------------------------
/** Applicant-facing labels: a submitted application is already "in review" from their point of view. */
export function customerStatusLabel(status: LoanStatus): string {
  switch (status) {
    case "submitted": return "En revisión";
    case "in_review": return "En revisión";
    case "approved": return "Aprobada";
    case "rejected": return "No aprobada";
  }
}

/** Partner-facing labels distinguish the untouched queue from what a reviewer already claimed. */
export function partnerStatusLabel(status: LoanStatus): string {
  switch (status) {
    case "submitted": return "Nueva";
    case "in_review": return "En revisión";
    case "approved": return "Aprobada";
    case "rejected": return "Rechazada";
  }
}

export function statusTone(status: LoanStatus): "pending" | "active" | "suspended" | "info" {
  switch (status) {
    case "submitted": return "info";
    case "in_review": return "pending";
    case "approved": return "active";
    case "rejected": return "suspended";
  }
}

export const TERM_OPTIONS: ReadonlyArray<{ months: LoanTerm; hint: string }> = [
  { months: 6, hint: "Menos intereses totales" },
  { months: 12, hint: "Pago mensual más bajo" }
];

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
